import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Star, MessageSquare, Reply, X, Send, CheckCircle2, Search } from "lucide-react";
import { sanitizeHtml } from "../../lib/safeMarkdown";

function StarRow({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`w-4 h-4 ${n <= rating ? "fill-[#C5A059] text-[#C5A059]" : "text-white/20"}`} />
      ))}
    </div>
  );
}

function ReplyModal({ review, onClose, onSent }) {
  const [subject, setSubject] = useState(`Re: your TaxiNearMe review (${review.user_name || "customer"})`);
  const [body, setBody] = useState(`<p>Gentile ${review.user_name || "Cliente"},</p>\n<p>Grazie per la tua recensione. ...</p>\n<p>Cordiali saluti,<br/>Team TaxiNearMe</p>`);
  const [sending, setSending] = useState(false);

  const previewHtml = useMemo(() => ({ __html: sanitizeHtml(body) }), [body]);

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject and body required"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/admin/reviews/${review.review_id}/reply`, { subject, body_html: body });
      toast.success("Reply sent");
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send reply");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="review-reply-modal">
      <div className="bg-[#121212] border border-white/10 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Reply className="w-5 h-5 text-[#C5A059]" /> Reply to review
            </h3>
            <p className="text-white/40 text-xs mt-1">{review.user_name} &lt;{review.user_email}&gt;</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="reply-close-btn"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Original review */}
          <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-3">
            <StarRow rating={review.rating} />
            {review.comment && <p className="text-white/70 text-sm italic mt-2">"{review.comment}"</p>}
          </div>

          <div>
            <label className="text-white/60 text-xs mb-1 block">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="input-dark" data-testid="reply-subject" />
          </div>
          <div>
            <label className="text-white/60 text-xs mb-1 block">Body (HTML allowed — placeholders {`{customer_name}`}, {`{actor_name}`} work)</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="w-full min-h-[180px] bg-[#1A1A1A] border border-[#333] rounded-sm p-3 text-white/80 text-xs font-mono resize-y focus:border-[#C5A059]/50 focus:outline-none"
              data-testid="reply-body"
            />
          </div>
          <div>
            <p className="text-white/40 text-[10px] uppercase mb-1">Preview</p>
            <div className="bg-white text-black p-3 rounded-sm border border-white/10 min-h-[100px] text-sm" data-testid="reply-preview" dangerouslySetInnerHTML={previewHtml} />
          </div>
          <Button onClick={send} disabled={sending} className="btn-gold w-full" data-testid="reply-send-btn">
            <Send className="w-4 h-4 mr-2" /> {sending ? "Sending..." : "Send reply"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [filterReplied, setFilterReplied] = useState("all"); // all | replied | pending

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/reviews`);
      setReviews(res.data || []);
    } catch (e) {
      console.error("Failed to load reviews", e);
      toast.error("Failed to load reviews");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return reviews.filter(r => {
      if (filterReplied === "replied" && !r.replied_at) return false;
      if (filterReplied === "pending" && r.replied_at) return false;
      if (!q) return true;
      return (
        (r.user_name || "").toLowerCase().includes(q) ||
        (r.user_email || "").toLowerCase().includes(q) ||
        (r.comment || "").toLowerCase().includes(q) ||
        (r.quote_id || "").toLowerCase().includes(q)
      );
    });
  }, [reviews, search, filterReplied]);

  const stats = useMemo(() => {
    if (reviews.length === 0) return { avg: 0, total: 0, pending: 0 };
    const total = reviews.length;
    const avg = reviews.reduce((s, r) => s + (r.rating || 0), 0) / total;
    const pending = reviews.filter(r => !r.replied_at).length;
    return { avg, total, pending };
  }, [reviews]);

  return (
    <div data-testid="reviews-tab" className="space-y-4">
      {/* Header / stats */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#C5A059]" /> Customer reviews
          </h2>
          <p className="text-white/40 text-xs mt-1">Sorted by most recent. Reply with a one-shot email.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-[#1F1F1F] border border-white/5 rounded-sm px-3 py-1.5">
            <p className="text-white/40 text-[10px] uppercase">Avg</p>
            <div className="flex items-center gap-1"><Star className="w-3 h-3 fill-[#C5A059] text-[#C5A059]" /><span className="text-[#C5A059] font-bold text-sm">{stats.avg.toFixed(1)}</span></div>
          </div>
          <div className="bg-[#1F1F1F] border border-white/5 rounded-sm px-3 py-1.5">
            <p className="text-white/40 text-[10px] uppercase">Total</p>
            <p className="text-white text-sm font-bold">{stats.total}</p>
          </div>
          <div className="bg-[#1F1F1F] border border-amber-500/20 rounded-sm px-3 py-1.5">
            <p className="text-amber-400 text-[10px] uppercase">No reply</p>
            <p className="text-amber-400 text-sm font-bold">{stats.pending}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, comment, ID..." className="input-dark pl-9" data-testid="reviews-search" />
        </div>
        <div className="flex items-center gap-1 bg-[#1F1F1F] border border-white/5 rounded-sm overflow-hidden">
          {["all", "pending", "replied"].map(opt => (
            <button
              key={opt}
              onClick={() => setFilterReplied(opt)}
              className={`px-3 py-1 text-xs uppercase transition-colors ${filterReplied === opt ? "bg-[#C5A059]/20 text-[#C5A059]" : "text-white/50 hover:text-white"}`}
              data-testid={`reviews-filter-${opt}`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-20"><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-white/40 text-sm italic text-center py-12">No reviews to show.</p>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => (
            <div key={r.review_id} className="bg-[#0F0F0F] border border-white/5 rounded-sm p-4 hover:border-[#C5A059]/30 transition-colors" data-testid={`review-row-${r.review_id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <StarRow rating={r.rating} />
                    <span className="text-white text-sm font-semibold">{r.user_name || "Customer"}</span>
                    <span className="text-white/40 text-xs">{r.user_email}</span>
                    <span className="text-white/30 text-[10px] font-mono">{r.quote_id}</span>
                  </div>
                  {r.comment && <p className="text-white/70 text-sm italic mt-2">"{r.comment}"</p>}
                  <p className="text-white/30 text-[10px] mt-2">{r.created_at?.replace("T", " ").slice(0, 19)}</p>
                  {r.replied_at && (
                    <p className="text-emerald-400 text-[10px] mt-1 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Replied {r.replied_at.slice(0, 10)} by {r.replied_by}
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  onClick={() => setReplyTo(r)}
                  variant={r.replied_at ? "ghost" : "default"}
                  className={r.replied_at ? "text-white/60 hover:text-[#C5A059]" : "bg-[#C5A059] hover:bg-[#E6C785] text-black"}
                  data-testid={`reply-btn-${r.review_id}`}
                >
                  <Reply className="w-4 h-4 mr-1" /> {r.replied_at ? "Reply again" : "Reply"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {replyTo && (
        <ReplyModal review={replyTo} onClose={() => setReplyTo(null)} onSent={load} />
      )}
    </div>
  );
}
